import { InputBaseProps } from "@mui/material";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/react";
import { QueryKey } from "@tanstack/react-query";
import { ReactNode } from "react";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import { useGet, UseGetOptions } from "@/hooks";

export type FormAsyncMultiSelectValue = Array<string | number>;

export type FormAsyncMultiSelectProps<TFormValues extends FieldValues, TData> = Partial<
  Omit<
    AutocompleteProps<TData, true, false, false>,
    "name" | "multiple" | "disableClearable" | "freeSolo" | "control" | "options" | "defaultValue"
  >
> & {
  name: Path<TFormValues>;
  getOptionLabel: (data: TData) => string;
  getOptionSecondaryLabel?: (data: TData) => string;
  getOptionIcon?: (data: TData) => ReactNode;
  getOptionValue: (data: TData) => number | string;
  getFilteredOptions?: (data: TData[], values: TFormValues) => TData[];
  sortBy?: (a: TData, b: TData) => number;
  endpoint: string;
  queryKey?: QueryKey;
  required?: TextFieldProps["required"];
  label?: TextFieldProps["label"];
  helperText?: TextFieldProps["helperText"];
  getOptions?: UseGetOptions<TData[]>;
  inputSlotProps?: InputBaseProps;
};

export function FormAsyncMultiSelect<TFormValues extends FieldValues, TData>({
  name,
  getOptionLabel,
  getOptionSecondaryLabel = () => "",
  getOptionIcon = () => null,
  getOptionValue,
  getFilteredOptions = (options) => options,
  sortBy,
  endpoint = "",
  queryKey = [endpoint],
  label = "",
  required = false,
  disabled = false,
  getOptions = {},
  inputSlotProps: inputProps = {},
  ...props
}: FormAsyncMultiSelectProps<TFormValues, TData>) {
  const { control, setValue, watch } = useFormContext<TFormValues>();
  const values = watch();

  const { data = [], isLoading } = useGet<TData[]>({
    endpoint,
    queryKey,
    options: getOptions,
  });

  const sortedData = sortBy ? data.sort(sortBy) : data;

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <Autocomplete<TData, true, false, false>
              disableCloseOnSelect
              value={sortedData.filter((item) => field.value.includes(getOptionValue(item)))}
              options={getFilteredOptions(sortedData, values)}
              loading={isLoading}
              getOptionLabel={(option) => getOptionLabel(option) ?? ""}
              disabled={disabled}
              fullWidth
              multiple
              onChange={(event, value) => {
                setValue(
                  field.name,
                  (!value
                    ? []
                    : value.map((item) => (item ? getOptionValue(item) : null)).filter(Boolean)) as PathValue<
                    TFormValues,
                    Path<TFormValues>
                  >
                );
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    name={field.name}
                    label={label}
                    required={required}
                    fullWidth
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        autoComplete: "off",
                      },
                      input: {
                        ...params.InputProps,
                        ...inputProps,
                        endAdornment: (
                          <>
                            {params.InputProps.endAdornment}
                            {inputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                return (
                  <Stack
                    component="li"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between !important"
                    spacing={2}
                    {...props}
                    key={props.id}
                  >
                    <Stack
                      sx={{ "& svg": { color: "primary.main", fontSize: 20 } }}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                    >
                      {getOptionIcon(option)}
                      <Stack direction="column">
                        <Typography variant="body1">{getOptionLabel(option)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getOptionSecondaryLabel(option)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                );
              }}
              {...props}
            />
          );
        }}
      />
    </Sentry.ErrorBoundary>
  );
}
